import { c as _c } from "react/compiler-runtime";
import type { BigIntish, ChainId, MarketId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { getIsPositionUnhealthy, type InterfaceToken, type MarketForRewards, type Point, type Rewards, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { useMemo } from 'react';
import { useMarketPoints } from '@/components/charts/hooks/useMarketPoints';
import { useProtectedLltv } from '@/components/common/AutoDeleverage/ProtectedLltv';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getParsedValue } from '@/helpers/formatters';
import { getNetRateOnMarket } from '@/helpers/getNativeApy';
import { getMarketRewards } from '@/helpers/rewards';

/** Current / after position figures, supplied by the caller (no simulation-sdk). */
export interface MarketSummaryPosition {
  collateral?: bigint;
  loan?: bigint;
  ltv?: bigint;
}
interface UseMarketSummaryDataProps {
  marketId: MarketId;
  chainId: ChainId;
  /** Current on-chain position. */
  initialData: MarketSummaryPosition;
  /** Position after the pending action (undefined fields ⇒ no "after" value shown). */
  finalData: MarketSummaryPosition;
  collateralAsset: InterfaceToken;
  loanAsset: InterfaceToken;
  lltv?: BigIntish;
  avgBorrowApy?: number | null;
  marketForRewards?: MarketForRewards | null;
}
interface PositionData {
  initial: string;
  initialDetailed?: string;
  final?: string;
  finalDetailed?: string;
  showFinalValue: boolean;
}
interface LtvData {
  initial: string;
  final?: string | null;
  showFinalValue: boolean;
  isInitialUnhealthy: boolean;
  isFinalUnhealthy: boolean;
}
interface ApyData {
  apy?: bigint | null;
  netApy?: bigint | null;
  formattedNetApy: string;
  rewards: Rewards[];
  points: Point[];
}
interface MarketSummaryData {
  collateral: PositionData;
  loan: PositionData;
  ltv: LtvData;
  apy: ApyData;
}
export const useMarketSummaryData = t0 => {
  const $ = _c(101);
  const {
    marketId,
    chainId,
    initialData,
    finalData,
    collateralAsset,
    loanAsset,
    lltv,
    avgBorrowApy,
    marketForRewards
  } = t0;
  let t1;
  if ($[0] !== chainId || $[1] !== lltv || $[2] !== marketId) {
    t1 = {
      lltv,
      marketId,
      chainId
    };
    $[0] = chainId;
    $[1] = lltv;
    $[2] = marketId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const protectedLltv = useProtectedLltv(t1);
  const {
    targetChainId
  } = useWeb3Context();
  let t2;
  if ($[4] !== marketForRewards) {
    t2 = getMarketRewards(marketForRewards);
    $[4] = marketForRewards;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const rewards = t2;
  let t3;
  if ($[6] !== collateralAsset.address || $[7] !== marketId || $[8] !== targetChainId) {
    t3 = {
      chainId: targetChainId,
      marketId,
      collateralAddress: collateralAsset.address
    };
    $[6] = collateralAsset.address;
    $[7] = marketId;
    $[8] = targetChainId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const points = useMarketPoints(t3);
  let t4;
  if ($[10] !== avgBorrowApy) {
    t4 = getParsedValue(avgBorrowApy, safeParseNumber);
    $[10] = avgBorrowApy;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const apy = t4;
  let t5;
  if ($[12] !== apy || $[13] !== rewards) {
    t5 = getNetRateOnMarket({
      rewards,
      nativeRate: apy
    });
    $[12] = apy;
    $[13] = rewards;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const netApy = t5;
  let t6;
  if ($[15] !== netApy) {
    t6 = formatters.percent.unit("%").digits(2).default("-").max(10).of(netApy, 18);
    $[15] = netApy;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const formattedBorrowNetApy = t6;
  let t7;
  if ($[17] !== collateralAsset.decimals || $[18] !== collateralAsset.price || $[19] !== initialData.collateral) {
    t7 = formatters.short.digits(getDigitsFromPriceAndAmount(collateralAsset.price, initialData.collateral, collateralAsset.decimals)).trailingZero(!initialData.collateral).default("-").of(initialData.collateral, collateralAsset.decimals);
    $[17] = collateralAsset.decimals;
    $[18] = collateralAsset.price;
    $[19] = initialData.collateral;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const formattedInitial = t7;
  let t8;
  if ($[21] !== collateralAsset.decimals || $[22] !== collateralAsset.price || $[23] !== collateralAsset.symbol || $[24] !== initialData.collateral) {
    t8 = initialData.collateral && initialData.collateral > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(collateralAsset.price, initialData.collateral, collateralAsset.decimals)).unit(collateralAsset.symbol).trailingZero(false).of(initialData.collateral, collateralAsset.decimals) : undefined;
    $[21] = collateralAsset.decimals;
    $[22] = collateralAsset.price;
    $[23] = collateralAsset.symbol;
    $[24] = initialData.collateral;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const formattedInitialDetailed = t8;
  let t9;
  if ($[26] !== collateralAsset.decimals || $[27] !== collateralAsset.price || $[28] !== finalData.collateral) {
    t9 = formatters.short.digits(getDigitsFromPriceAndAmount(collateralAsset.price, finalData.collateral, collateralAsset.decimals)).trailingZero(!finalData.collateral).of(finalData.collateral, collateralAsset.decimals);
    $[26] = collateralAsset.decimals;
    $[27] = collateralAsset.price;
    $[28] = finalData.collateral;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const formattedFinal = t9;
  let t10;
  if ($[30] !== collateralAsset.decimals || $[31] !== collateralAsset.price || $[32] !== collateralAsset.symbol || $[33] !== finalData.collateral) {
    t10 = finalData.collateral && finalData.collateral > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(collateralAsset.price, finalData.collateral, collateralAsset.decimals)).unit(collateralAsset.symbol).trailingZero(false).of(finalData.collateral, collateralAsset.decimals) : undefined;
    $[30] = collateralAsset.decimals;
    $[31] = collateralAsset.price;
    $[32] = collateralAsset.symbol;
    $[33] = finalData.collateral;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const formattedFinalDetailed = t10;
  let t11;
  if ($[35] !== finalData.collateral || $[36] !== initialData.collateral) {
    t11 = isDefined(finalData.collateral) && finalData.collateral !== initialData.collateral;
    $[35] = finalData.collateral;
    $[36] = initialData.collateral;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== formattedFinal || $[39] !== formattedFinalDetailed || $[40] !== formattedInitial || $[41] !== formattedInitialDetailed || $[42] !== t11) {
    t12 = {
      initial: formattedInitial,
      final: formattedFinal,
      initialDetailed: formattedInitialDetailed,
      finalDetailed: formattedFinalDetailed,
      showFinalValue: t11
    };
    $[38] = formattedFinal;
    $[39] = formattedFinalDetailed;
    $[40] = formattedInitial;
    $[41] = formattedInitialDetailed;
    $[42] = t11;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const collateral = t12;
  let t13;
  if ($[44] !== initialData.loan || $[45] !== loanAsset.decimals || $[46] !== loanAsset.price) {
    t13 = formatters.short.digits(getDigitsFromPriceAndAmount(loanAsset.price, initialData.loan, loanAsset.decimals)).trailingZero(!initialData.loan).default("-").of(initialData.loan, loanAsset.decimals);
    $[44] = initialData.loan;
    $[45] = loanAsset.decimals;
    $[46] = loanAsset.price;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const formattedInitial_0 = t13;
  let t14;
  if ($[48] !== initialData.loan || $[49] !== loanAsset.decimals || $[50] !== loanAsset.price || $[51] !== loanAsset.symbol) {
    t14 = initialData.loan && initialData.loan > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(loanAsset.price, initialData.loan)).trailingZero(false).unit(loanAsset.symbol).of(initialData.loan, loanAsset.decimals) : undefined;
    $[48] = initialData.loan;
    $[49] = loanAsset.decimals;
    $[50] = loanAsset.price;
    $[51] = loanAsset.symbol;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  const formattedInitialDetailed_0 = t14;
  let t15;
  if ($[53] !== finalData.loan || $[54] !== loanAsset.decimals || $[55] !== loanAsset.price) {
    t15 = formatters.short.digits(getDigitsFromPriceAndAmount(loanAsset.price, finalData.loan, loanAsset.decimals)).trailingZero(!finalData.loan).default("-").of(finalData.loan, loanAsset.decimals);
    $[53] = finalData.loan;
    $[54] = loanAsset.decimals;
    $[55] = loanAsset.price;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  const formattedFinal_0 = t15;
  let t16;
  if ($[57] !== finalData.loan || $[58] !== loanAsset.decimals || $[59] !== loanAsset.price || $[60] !== loanAsset.symbol) {
    t16 = finalData.loan && finalData.loan > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(loanAsset.price, finalData.loan, loanAsset.decimals)).trailingZero(false).unit(loanAsset.symbol).of(finalData.loan, loanAsset.decimals) : undefined;
    $[57] = finalData.loan;
    $[58] = loanAsset.decimals;
    $[59] = loanAsset.price;
    $[60] = loanAsset.symbol;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  const formattedFinalDetailed_0 = t16;
  let t17;
  if ($[62] !== finalData.loan || $[63] !== initialData.loan) {
    t17 = isDefined(finalData.loan) && finalData.loan !== initialData.loan;
    $[62] = finalData.loan;
    $[63] = initialData.loan;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  let t18;
  if ($[65] !== formattedFinalDetailed_0 || $[66] !== formattedFinal_0 || $[67] !== formattedInitialDetailed_0 || $[68] !== formattedInitial_0 || $[69] !== t17) {
    t18 = {
      initial: formattedInitial_0,
      final: formattedFinal_0,
      initialDetailed: formattedInitialDetailed_0,
      finalDetailed: formattedFinalDetailed_0,
      showFinalValue: t17
    };
    $[65] = formattedFinalDetailed_0;
    $[66] = formattedFinal_0;
    $[67] = formattedInitialDetailed_0;
    $[68] = formattedInitial_0;
    $[69] = t17;
    $[70] = t18;
  } else {
    t18 = $[70];
  }
  const loan = t18;
  let t19;
  if ($[71] !== initialData.ltv) {
    t19 = formatters.percent.unit("%").default("0%").trailingZero(false).digits(2).max(1).of(initialData.ltv, 18);
    $[71] = initialData.ltv;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  const formattedInitial_1 = t19;
  let t20;
  if ($[73] !== finalData.ltv) {
    t20 = formatters.percent.unit("%").trailingZero(false).digits(2).default(null).max(1).of(finalData.ltv, 18);
    $[73] = finalData.ltv;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  const formattedFinal_1 = t20;
  let t21;
  if ($[75] !== initialData.ltv || $[76] !== protectedLltv) {
    t21 = initialData.ltv && protectedLltv ? getIsPositionUnhealthy(initialData.ltv, protectedLltv) : false;
    $[75] = initialData.ltv;
    $[76] = protectedLltv;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  const isInitialUnhealthy = t21;
  let t22;
  if ($[78] !== finalData.ltv || $[79] !== protectedLltv) {
    t22 = finalData.ltv && protectedLltv ? getIsPositionUnhealthy(finalData.ltv, protectedLltv) : false;
    $[78] = finalData.ltv;
    $[79] = protectedLltv;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  const isFinalUnhealthy = t22;
  let t23;
  if ($[81] !== finalData.ltv || $[82] !== initialData.ltv) {
    t23 = isDefined(finalData.ltv) && finalData.ltv !== initialData.ltv;
    $[81] = finalData.ltv;
    $[82] = initialData.ltv;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  let t24;
  if ($[84] !== formattedFinal_1 || $[85] !== formattedInitial_1 || $[86] !== isFinalUnhealthy || $[87] !== isInitialUnhealthy || $[88] !== t23) {
    t24 = {
      initial: formattedInitial_1,
      final: formattedFinal_1,
      showFinalValue: t23,
      isInitialUnhealthy,
      isFinalUnhealthy
    };
    $[84] = formattedFinal_1;
    $[85] = formattedInitial_1;
    $[86] = isFinalUnhealthy;
    $[87] = isInitialUnhealthy;
    $[88] = t23;
    $[89] = t24;
  } else {
    t24 = $[89];
  }
  const ltv = t24;
  let t25;
  if ($[90] !== apy || $[91] !== formattedBorrowNetApy || $[92] !== netApy || $[93] !== points || $[94] !== rewards) {
    t25 = {
      apy,
      netApy,
      formattedNetApy: formattedBorrowNetApy,
      rewards,
      points
    };
    $[90] = apy;
    $[91] = formattedBorrowNetApy;
    $[92] = netApy;
    $[93] = points;
    $[94] = rewards;
    $[95] = t25;
  } else {
    t25 = $[95];
  }
  let t26;
  if ($[96] !== collateral || $[97] !== loan || $[98] !== ltv || $[99] !== t25) {
    t26 = {
      collateral,
      loan,
      ltv,
      apy: t25
    };
    $[96] = collateral;
    $[97] = loan;
    $[98] = ltv;
    $[99] = t25;
    $[100] = t26;
  } else {
    t26 = $[100];
  }
  return t26;
};